import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Box,
  Stack,
  Chip,
  Button,
  Divider,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  Checkbox,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import PageHero from '../components/editorial/PageHero';
import InvoiceCheckout from '../components/stripe/InvoiceCheckout';
import StatementTable from '../components/account/StatementTable';
import GivingStatementPrint from '../components/account/GivingStatementPrint';
import AccountNav from '../components/account/AccountNav';
import AccountPanel from '../components/account/AccountPanel';
import AccountSeatsForm from '../components/account/AccountSeatsForm';
import AccountMembershipSummary, {
  purchasedSeatCount,
} from '../components/account/AccountMembershipSummary';
import { currency } from '../components/account/currency';
import PaymentPlanSetup from '../components/paymentPlan/PaymentPlanSetup';
import PaymentPlanCheckout from '../components/paymentPlan/PaymentPlanCheckout';
import HouseholdForm from '../components/membership/HouseholdForm';

import { authedFetch, waitForAuthUser } from '../api/authedFetch';
import { emptyHousehold } from '../service/householdValidation';
import { stashReturnTo } from '../service/returnTo';
import { canPayAll, getPayAllSummary } from '../utils/payAllInvoices';
import { createPaymentPlan, getPaymentPlans, cancelPaymentPlan } from '../api/payments/paymentPlans';
import { getHighHolidays, confirmAttendance, createExtraSeatsIntent } from '../api/me/highHolidays';
import { seatRowsToBlock } from '../service/highHolidaySeats';

const ACCOUNT_NAV_ITEMS = [
  { id: 'household', label: 'Household & Family' },
  { id: 'seats', label: 'High Holiday Seats' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'giving-statement', label: 'Statement' },
];

const statusColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'overdue':
      return 'error';
    case 'paid':
      return 'success';
    default:
      return 'default';
  }
};

const cardSx = {
  border: '1px solid rgba(36,34,88,0.15)',
  background: 'var(--paper)',
  p: { xs: 2.5, md: 3 },
};

const ACCOUNT_SECTION_IDS = ACCOUNT_NAV_ITEMS.map((item) => item.id);
const LEGACY_ACCOUNT_SECTIONS = { 'personal-info': 'household' };

const resolveAccountSection = (requested) => {
  const resolved = LEGACY_ACCOUNT_SECTIONS[requested] || requested;
  return ACCOUNT_SECTION_IDS.includes(resolved) ? resolved : 'household';
};

const addressDefaultsFromHousehold = (household) => {
  if (
    !household?.address?.trim() ||
    !household?.city?.trim() ||
    !household?.state?.trim() ||
    !household?.zip?.trim()
  ) {
    return null;
  }
  return {
    name: household.householdName || undefined,
    address: {
      line1: household.address,
      city: household.city,
      state: household.state,
      postal_code: household.zip,
      country: 'US',
    },
  };
};

export default function MyAccount() {
  const navigate = useNavigate();

  // Which single section the dashboard shows — read from ?section= on
  // first load (so a bookmarked/shared link can land on a specific
  // section) and kept in sync with the URL (without a full route change)
  // as the member switches tabs.
  const [activeSection, setActiveSection] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('section');
    return resolveAccountSection(requested);
  });

  const selectSection = (id) => {
    const next = resolveAccountSection(id);
    setActiveSection(next);
    const params = new URLSearchParams(window.location.search);
    params.set('section', next);
    navigate({ search: params.toString() }, { replace: true });
  };

  // Bookmarks/emails that still deep-link to the retired Personal Info
  // tab land on Household & Family instead of a blank panel.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('section') !== 'personal-info') return undefined;
    params.set('section', 'household');
    navigate({ search: params.toString() }, { replace: true });
    return undefined;
  }, [navigate]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qbUnavailable, setQbUnavailable] = useState(false);
  const [data, setData] = useState({ open: [], paid: [], outstandingTotal: 0 });

  // Pay dialog state
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [payingAll, setPayingAll] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [payError, setPayError] = useState(null);

  // Payment plans (KBT-170): multi-invoice selection + setup + list/cancel
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [planSubmitting, setPlanSubmitting] = useState(false);
  const [planError, setPlanError] = useState(null);
  const [planClientSecret, setPlanClientSecret] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansError, setPlansError] = useState(null);
  const [cancelingPlanId, setCancelingPlanId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  // Giving history (live from QuickBooks)
  const [statement, setStatement] = useState(null);
  const [statementError, setStatementError] = useState(null);
  const [statementYear, setStatementYear] = useState('all');

  // Household & Family is the single profile surface. `profileComplete`
  // is null until loaded so the soft-prompt banner never flashes on first
  // render; existing members missing household fields keep full portal access.
  const [profileComplete, setProfileComplete] = useState(null);
  const [account, setAccount] = useState({
    email: null,
    displayName: null,
    phoneNumber: null,
    phoneVerified: false,
  });
  const [membership, setMembership] = useState(null);

  // Household & Family — the same fields Membership 5787's HouseholdForm
  // collects at signup (GET/PUT /api/me/household). `household` starts
  // blank so a member who's never enrolled still gets a fillable form
  // rather than an error.
  const [household, setHousehold] = useState(emptyHousehold());
  const [savingHousehold, setSavingHousehold] = useState(false);

  // High Holiday Seats. Seat NAMES no longer travel through
  // /me/household — GET /me/high-holidays owns attendance, the included
  // allowance and the paid (purchased) seats, and POST
  // /me/high-holidays/attendance is the only writer of included names.
  // `highHolidays` stays null if that load soft-fails, which
  // AccountSeatsForm renders as a "couldn't load, email the office" note.
  const [highHolidays, setHighHolidays] = useState(null);
  const [seatClientSecret, setSeatClientSecret] = useState(null);
  const [savingSeats, setSavingSeats] = useState(false);
  const [seatsError, setSeatsError] = useState(null);
  const hhRetryScheduled = useRef(false);

  const loadHousehold = useCallback(async () => {
    try {
      const res = await authedFetch('/me/household');
      if (!res.ok) throw new Error(`Could not load your household info (${res.status})`);
      const json = await res.json();
      if (json.household) setHousehold(json.household);
      if (json.account) setAccount(json.account);
      if (json.membership !== undefined) setMembership(json.membership);
      setProfileComplete(Boolean(json.profileComplete));
    } catch (err) {
      // Soft-fail: household editing isn't required to view invoices/statement.
      console.error('Error loading household info:', err);
    }
  }, []);

  const saveHousehold = async () => {
    setSavingHousehold(true);
    try {
      const payload = {
        ...household,
        ...(account.email ? { email: account.email } : {}),
      };
      const res = await authedFetch('/me/household', {
        method: 'PUT',
        body: JSON.stringify({ household: payload }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.msg || `Could not save your household info (${res.status})`);
      setHousehold(json.household);
      setProfileComplete(true);
      setAccount((prev) => ({
        ...prev,
        displayName: json.household?.householdName || prev.displayName,
        phoneNumber: json.household?.phone || prev.phoneNumber,
        phoneVerified:
          (json.household?.phone || '').trim() === (prev.phoneNumber || '').trim()
            ? prev.phoneVerified
            : false,
      }));
      toast.success('Household info updated!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingHousehold(false);
    }
  };

  const loadHighHolidays = useCallback(async () => {
    try {
      const next = await getHighHolidays();
      setHighHolidays(next);
      if (next?.membership) setMembership(next.membership);
    } catch (err) {
      // Soft-fail, same as household/profile: a load failure here must not
      // take down Invoices or Statement.
      console.error('Error loading High Holiday details:', err);
    }
  }, []);

  // The ONLY writer of included seat names. It deliberately does not go
  // through PUT /me/household any more: that endpoint's guard rejects any
  // change to the seat total, and having two paths write seats is how a
  // paid seat gets rewritten as an included one.
  const saveAttendance = async ({ attending, menSeatNames, womenSeatNames }) => {
    setSavingSeats(true);
    setSeatsError(null);
    try {
      const json = await confirmAttendance({ attending, menSeatNames, womenSeatNames });
      setHighHolidays((prev) => ({ ...prev, ...json }));
      toast.success(attending ? 'Attendance confirmed!' : 'Attendance updated.');
    } catch (err) {
      setSeatsError(err.message);
    } finally {
      setSavingSeats(false);
    }
  };

  const startSeatPurchase = async (seatRows) => {
    setSeatsError(null);
    try {
      const json = await createExtraSeatsIntent(seatRowsToBlock(seatRows));
      setSeatClientSecret(json.clientSecret);
    } catch (err) {
      setSeatsError(err.message);
    }
  };

  const loadInvoices = useCallback(async () => {
    try {
      const res = await authedFetch('/me/invoices');
      if (!res.ok) throw new Error(`Could not load invoices (${res.status})`);
      const json = await res.json();
      setData({
        open: json.open || [],
        paid: json.paid || [],
        outstandingTotal: json.outstandingTotal || 0,
      });
      setQbUnavailable(Boolean(json.qbUnavailable));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const loadStatement = useCallback(async () => {
    try {
      const res = await authedFetch('/me/statement');
      if (!res.ok) throw new Error(`Could not load your statement (${res.status})`);
      setStatement(await res.json());
      setStatementError(null);
    } catch (err) {
      setStatementError(err.message);
    }
  }, []);

  const loadPlans = useCallback(async () => {
    try {
      setPlans(await getPaymentPlans());
      setPlansError(null);
    } catch (err) {
      setPlansError(err.message);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const user = await waitForAuthUser();
      if (cancelled) return;
      if (!user) {
        // Not signed in — send home, where the header offers Sign In, but
        // remember where they were heading first. Every Give Butter
        // activation email deep-links to ?section=seats, and a logged-out
        // click used to discard that and dump them on the homepage with no
        // route back. The header consumes this once sign-in completes; a
        // storage failure just loses the destination and lands them home
        // as before.
        stashReturnTo(`${window.location.pathname}${window.location.search}`);
        navigate('/', { replace: true });
        return;
      }
      await Promise.all([
        loadInvoices(),
        loadStatement(),
        loadPlans(),
        loadHousehold(),
        loadHighHolidays(),
      ]);
      if (!cancelled) setLoading(false);
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [navigate, loadInvoices, loadStatement, loadPlans, loadHousehold, loadHighHolidays]);

  // Invite signup can land here before the account doc has `uid`. The
  // seats endpoint now returns accountPending instead of 404; retry a
  // few times so the form fills in without making the member refresh.
  useEffect(() => {
    if (loading || hhRetryScheduled.current) return undefined;
    if (highHolidays && !highHolidays.accountPending) return undefined;
    hhRetryScheduled.current = true;
    const timers = [500, 1500, 4000].map((ms) => setTimeout(loadHighHolidays, ms));
    return () => timers.forEach(clearTimeout);
  }, [loading, highHolidays, loadHighHolidays]);

  // After returning from Stripe (?redirect_status=succeeded), refetch a
  // few times so the invoice/plan flips to paid/active once the webhook lands.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('redirect_status') !== 'succeeded') return undefined;

    const refetch = () => {
      loadInvoices();
      loadPlans();
    };
    const timers = [2000, 5000, 10000].map((ms) => setTimeout(refetch, ms));
    return () => timers.forEach(clearTimeout);
  }, [loadInvoices, loadPlans]);

  // Extra-seat checkout now returns to /membership2026/thank-you?context=
  // extra-seats. Keep this refetch for anyone who still lands here with
  // Stripe's payment_intent params (old return_url, or a bookmark): the
  // webhook writes the seats, so we poll until they appear instead of
  // showing an empty state that reads like a failure.
  const returnedFromSeatPayment = useMemo(
    () =>
      activeSection === 'seats' &&
      Boolean(new URLSearchParams(window.location.search).get('payment_intent_client_secret')),
    [activeSection]
  );

  useEffect(() => {
    if (!returnedFromSeatPayment) return undefined;
    loadHighHolidays();
    const timers = [2000, 5000, 10000].map((ms) => setTimeout(loadHighHolidays, ms));
    return () => timers.forEach(clearTimeout);
  }, [returnedFromSeatPayment, loadHighHolidays]);

  const toggleInvoiceSelected = (invoiceId) => {
    setSelectedInvoiceIds((prev) =>
      prev.includes(invoiceId) ? prev.filter((id) => id !== invoiceId) : [...prev, invoiceId]
    );
  };

  const clearSelection = () => {
    setSelectedInvoiceIds([]);
    setPlanError(null);
  };

  const selectedInvoices = useMemo(
    () => data.open.filter((inv) => selectedInvoiceIds.includes(inv.id)),
    [data.open, selectedInvoiceIds]
  );

  const startPaymentPlan = async (installments) => {
    setPlanSubmitting(true);
    setPlanError(null);
    try {
      const result = await createPaymentPlan(selectedInvoiceIds, installments);
      setPlanClientSecret(result.clientSecret);
    } catch (err) {
      setPlanError(err.message);
    } finally {
      setPlanSubmitting(false);
    }
  };

  const closePlanDialog = () => {
    setPlanClientSecret(null);
    setSelectedInvoiceIds([]);
    loadInvoices();
    loadPlans();
  };

  const confirmCancelPlan = async () => {
    if (!cancelTarget) return;
    setCancelingPlanId(cancelTarget.id);
    try {
      await cancelPaymentPlan(cancelTarget.id);
      setCancelTarget(null);
      await Promise.all([loadPlans(), loadInvoices()]);
    } catch (err) {
      setPlansError(err.message);
    } finally {
      setCancelingPlanId(null);
    }
  };

  const startPayment = async (invoice) => {
    setPayingInvoice(invoice);
    setPayingAll(false);
    setClientSecret(null);
    setPayError(null);
    try {
      const res = await authedFetch(`/me/invoices/${invoice.id}/pay-intent`, {
        method: 'POST',
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.msg || `Payment setup failed (${res.status})`);
      setClientSecret(json.clientSecret);
    } catch (err) {
      setPayError(err.message);
    }
  };

  // "Pay all": combines every currently-open invoice into a single
  // Stripe charge for the summed total. The backend re-derives the
  // total server-side from the member's own open invoices (never
  // trusts a client-supplied amount) and, on success, posts a
  // QuickBooks payment against each underlying invoice individually.
  const startPayAll = async () => {
    setPayingInvoice({ id: 'pay-all' });
    setPayingAll(true);
    setClientSecret(null);
    setPayError(null);
    try {
      const res = await authedFetch('/me/invoices/pay-all-intent', {
        method: 'POST',
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.msg || `Payment setup failed (${res.status})`);
      setClientSecret(json.clientSecret);
    } catch (err) {
      setPayError(err.message);
    }
  };

  const closePayDialog = () => {
    setPayingInvoice(null);
    setPayingAll(false);
    setClientSecret(null);
    setPayError(null);
  };

  const statementYears = useMemo(() => {
    const years = new Set(
      (statement?.transactions || [])
        .map((t) => (t.date || '').slice(0, 4))
        .filter(Boolean)
    );
    return Array.from(years).sort().reverse();
  }, [statement]);

  const visibleTransactions = useMemo(() => {
    const all = statement?.transactions || [];
    if (statementYear === 'all') return all;
    return all.filter((t) => (t.date || '').startsWith(statementYear));
  }, [statement, statementYear]);

  const donationTotal = useMemo(
    () =>
      visibleTransactions
        .filter((t) => t.type === 'donation')
        .reduce((sum, t) => sum + (t.amount || 0), 0),
    [visibleTransactions]
  );

  const paymentTotal = useMemo(
    () =>
      visibleTransactions
        .filter((t) => t.type !== 'donation')
        .reduce((sum, t) => sum + (t.amount || 0), 0),
    [visibleTransactions]
  );

  const printStatement = () => {
    document.body.classList.add('print-statement');
    const cleanup = () => {
      document.body.classList.remove('print-statement');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
  };

  const renderInvoiceRow = (invoice, { payable }) => (
    <Box key={invoice.id} sx={cardSx}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          {payable && (
            <Checkbox
              checked={selectedInvoiceIds.includes(invoice.id)}
              onChange={() => toggleInvoiceSelected(invoice.id)}
              disabled={invoice.inPaymentPlan}
              sx={{ mt: -0.5 }}
              inputProps={{ 'aria-label': `Select ${invoice.invoiceNumber || 'invoice'} for a payment plan` }}
            />
          )}
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography fontWeight={600}>
                {invoice.invoiceNumber || 'Invoice'}
              </Typography>
              <Chip
                size="small"
                label={invoice.status}
                color={statusColor(invoice.status)}
                sx={{ textTransform: 'capitalize' }}
              />
              {invoice.inPaymentPlan && (
                <Chip size="small" label="In payment plan" color="info" variant="outlined" />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {invoice.category}
              {invoice.dueDate && payable ? `  ·  due ${invoice.dueDate}` : ''}
            </Typography>
            {(invoice.lineItems || []).map((li, i) => (
              <Typography key={i} variant="body2" color="text.secondary">
                {li.description}
              </Typography>
            ))}
          </Box>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography fontWeight={700} fontSize={18}>
            {currency(invoice.total)}
          </Typography>
          {payable && !invoice.inPaymentPlan && (
            <Button
              variant="contained"
              onClick={() => startPayment(invoice)}
              sx={{
                bgcolor: '#242258',
                borderRadius: '60px',
                px: 3,
                '&:hover': { bgcolor: '#1a1840' },
              }}
            >
              Pay
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );

  return (
    <EditorialPageShell
      title="My Account"
      description="Your invoices and payments"
    >
      <PageHero
        title="My"
        titleAccent="Account"
        subtitle="Your invoices, in one place."
        height="46vh"
        minHeight={380}
      />

      {/* Soft prompt (not a hard gate): existing members who haven't
          filled these in yet keep full access to everything below. */}
      {!loading && profileComplete === false && (
        <Box sx={{ px: { xs: 2.5, md: 4 }, pt: 3, maxWidth: 1200, mx: 'auto' }}>
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" onClick={() => selectSection('household')}>
                Complete profile
              </Button>
            }
          >
            <AlertTitle>Finish setting up your profile</AlertTitle>
            Please add your household details so we can keep your membership
            records complete.
          </Alert>
        </Box>
      )}

      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          py: { xs: 3, md: 6 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'flex-start',
          gap: { xs: 0, md: 6 },
        }}
      >
        <AccountNav items={ACCOUNT_NAV_ITEMS} activeId={activeSection} onSelect={selectSection} />

        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {activeSection === 'household' && (
            <AccountPanel
              kicker="Member Portal"
              title="Household & Family"
              intro="Keep this current so we can plan seating, kids' programming, and yahrzeit reminders."
            >
              <AccountMembershipSummary
                membership={membership}
                guestSeatCount={purchasedSeatCount(highHolidays?.purchasedSeats)}
                onManageSeats={() => selectSection('seats')}
              />
              <HouseholdForm
                household={household}
                onChange={setHousehold}
                onContinue={saveHousehold}
                submitLabel="Save changes"
                submitting={savingHousehold}
                lockedEmail={account.email}
                phoneVerified={Boolean(account.phoneVerified)}
              />
            </AccountPanel>
          )}

          {activeSection === 'seats' && (
            <AccountPanel
              kicker="Member Portal"
              title="High Holiday Seats"
              intro={
                highHolidays?.isMember === false
                  ? "High Holiday seats are $360 each. Become a member for included seats and the member rate."
                  : highHolidays?.membership?.tierName
                  ? `Your ${highHolidays.membership.tierName} membership. Confirm who's coming, name the seats it includes, and add guest seats at the member rate.`
                  : "Let us know whether you'll be joining us, tell us who's sitting in the seats your membership includes, and add guest seats if you need them."
              }
            >
              <AccountSeatsForm
                highHolidays={highHolidays}
                onConfirmAttendance={saveAttendance}
                onStartSeatPurchase={startSeatPurchase}
                onCancelSeatPurchase={() => setSeatClientSecret(null)}
                clientSecret={seatClientSecret}
                saving={savingSeats}
                error={seatsError}
                returnedFromPayment={returnedFromSeatPayment}
              />
            </AccountPanel>
          )}

          {activeSection === 'invoices' && (
            <AccountPanel
              kicker="Member Portal"
              title="Invoices"
              intro={loading ? undefined : `Outstanding balance: ${currency(data.outstandingTotal)}`}
            >
              {loading ? (
                <Stack alignItems="center" py={8}>
                  <CircularProgress sx={{ color: 'var(--navy-500)' }} />
                </Stack>
              ) : error ? (
                <Typography color="error">{error}</Typography>
              ) : (
                <Stack spacing={4}>
                  <Stack spacing={2}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      spacing={1.5}
                    >
                      <Typography variant="overline" color="text.secondary">
                        Open invoices
                      </Typography>
                      {canPayAll(data.open) && (
                        <Button
                          variant="contained"
                          onClick={startPayAll}
                          sx={{
                            bgcolor: '#242258',
                            borderRadius: '60px',
                            px: 3,
                            '&:hover': { bgcolor: '#1a1840' },
                          }}
                        >
                          Pay all ({currency(getPayAllSummary(data.open).total)})
                        </Button>
                      )}
                    </Stack>
                    {data.open.length === 0 ? (
                      <Typography color="text.secondary">
                        {qbUnavailable
                          ? "Invoices are temporarily unavailable. Refresh in a moment — or email the office if this continues."
                          : "You have no open invoices. Thank you!"}
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Check off invoices below to pay them over time with a monthly payment plan,
                          instead of paying in full.
                        </Typography>
                        {data.open.map((inv) => renderInvoiceRow(inv, { payable: true }))}
                      </>
                    )}
                    <PaymentPlanSetup
                      selectedInvoices={selectedInvoices}
                      onSubmit={startPaymentPlan}
                      onClear={clearSelection}
                      submitting={planSubmitting}
                      error={planError}
                    />
                  </Stack>

                  {plans.length > 0 && (
                    <>
                      <Divider />
                      <Stack spacing={2}>
                        <Typography variant="overline" color="text.secondary">
                          Your payment plans
                        </Typography>
                        {plansError && <Typography color="error">{plansError}</Typography>}
                        {plans.map((plan) => (
                          <Box key={plan.id} sx={cardSx}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              justifyContent="space-between"
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                              spacing={2}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                  <Typography fontWeight={600}>
                                    {currency(plan.installmentAmount)}/month · {plan.installments}{' '}
                                    installments
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={plan.status}
                                    color={
                                      plan.status === 'completed'
                                        ? 'success'
                                        : plan.status === 'canceled'
                                        ? 'default'
                                        : 'info'
                                    }
                                    sx={{ textTransform: 'capitalize' }}
                                  />
                                  {plan.needsReconciliation && (
                                    <Chip size="small" label="Needs office follow-up" color="warning" />
                                  )}
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {plan.paymentsPosted} of {plan.installments} paid · remaining balance{' '}
                                  {currency(plan.remainingAmount)}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(100, (plan.paymentsPosted / plan.installments) * 100)}
                                  sx={{ mt: 1, borderRadius: 1 }}
                                />
                              </Box>
                              {['incomplete', 'active'].includes(plan.status) && (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  disabled={cancelingPlanId === plan.id}
                                  onClick={() => setCancelTarget(plan)}
                                  sx={{ borderRadius: '60px' }}
                                >
                                  Cancel plan
                                </Button>
                              )}
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </>
                  )}

                  <Divider />

                  <Stack spacing={2}>
                    <Typography variant="overline" color="text.secondary">
                      Paid invoices
                    </Typography>
                    {data.paid.length === 0 ? (
                      <Typography color="text.secondary">No paid invoices yet.</Typography>
                    ) : (
                      data.paid.map((inv) => renderInvoiceRow(inv, { payable: false }))
                    )}
                  </Stack>
                </Stack>
              )}
            </AccountPanel>
          )}

          {activeSection === 'giving-statement' && (
            <AccountPanel kicker="Giving History" title="Statement">
              {loading ? null : statementError ? (
                <Typography color="error">{statementError}</Typography>
              ) : !statement ? null : !statement.linked ? (
                <Typography color="text.secondary">{statement.msg}</Typography>
              ) : (
                <Box>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={2}
                    className="statement-controls"
                  >
                    <Select
                      size="small"
                      value={statementYear}
                      onChange={(e) => setStatementYear(e.target.value)}
                      sx={{ minWidth: 140, background: '#fff' }}
                    >
                      <MenuItem value="all">All years</MenuItem>
                      {statementYears.map((y) => (
                        <MenuItem key={y} value={y}>
                          {y}
                        </MenuItem>
                      ))}
                    </Select>
                    <Button
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      onClick={printStatement}
                      sx={{
                        borderRadius: '60px',
                        borderColor: '#242258',
                        color: '#242258',
                      }}
                    >
                      Print statement
                    </Button>
                  </Stack>

                  <StatementTable
                    transactions={visibleTransactions}
                    statementYear={statementYear}
                    donationTotal={donationTotal}
                    paymentTotal={paymentTotal}
                  />
                </Box>
              )}
            </AccountPanel>
          )}
        </Box>
      </Box>

      {/* Printable copy of the statement, portaled onto <body> so printing
          it doesn't depend on (and can't be clipped or mispositioned by)
          EditorialSection's overflow/position styling above. */}
      {!loading && !statementError && statement?.linked && (
        <GivingStatementPrint
          statement={statement}
          statementYear={statementYear}
          transactions={visibleTransactions}
          donationTotal={donationTotal}
          paymentTotal={paymentTotal}
        />
      )}

      <Dialog
        open={Boolean(payingInvoice)}
        onClose={closePayDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pr: 6 }}>
          {payingAll
            ? `Pay all ${getPayAllSummary(data.open).count} invoices — ${currency(
                getPayAllSummary(data.open).total
              )}`
            : `Pay ${payingInvoice?.invoiceNumber || 'invoice'} — ${currency(payingInvoice?.total)}`}
          <IconButton
            onClick={closePayDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {payError ? (
            <Typography color="error" py={2}>
              {payError}
            </Typography>
          ) : clientSecret ? (
            <InvoiceCheckout
              clientSecret={clientSecret}
              addressDefaults={addressDefaultsFromHousehold(household)}
            />
          ) : (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={28} />
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(planClientSecret)}
        onClose={closePlanDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pr: 6 }}>
          Confirm your payment plan
          <IconButton
            onClick={closePlanDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <PaymentPlanCheckout
            clientSecret={planClientSecret}
            addressDefaults={addressDefaultsFromHousehold(household)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(cancelTarget)} onClose={() => setCancelTarget(null)} maxWidth="sm">
        <DialogTitle>Cancel this payment plan?</DialogTitle>
        <DialogContent>
          <Typography>
            No further installments will be charged. Any remaining balance on the invoices in
            this plan will simply show as outstanding again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)}>Keep plan</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmCancelPlan}
            disabled={Boolean(cancelingPlanId)}
          >
            {cancelingPlanId ? 'Canceling…' : 'Cancel plan'}
          </Button>
        </DialogActions>
      </Dialog>
    </EditorialPageShell>
  );
}
